/**
 * O vocabulário do lucro por lead — e a ÚNICA aritmética que a tela tem direito de fazer.
 *
 * Duas coisas moram aqui, e elas são de naturezas diferentes:
 *
 *  1. **Texto e classificação** (faixas, rótulos, frases de vazio). Nada disso decide nada:
 *     a faixa em que o dia caiu chega pronta do servidor no campo `band`, e este arquivo só
 *     escolhe como ela se chama em português, que ícone a acompanha e que tom ela tem.
 *     Os LIMITES das faixas (quanto é "baixo", quanto é "ideal") **não estão escritos aqui
 *     de propósito**: número cravado na tela é texto fixo fingindo saber um estado do
 *     servidor — no dia em que o servidor mudar a régua, a tela vira mentira e ninguém
 *     percebe, porque ela continua desenhando.
 *
 *  2. **O eco do que a pessoa está digitando.** `centavosDeDigitos` e `previaLucroPorLead`
 *     fazem conta, sim, e é a exceção que a invariante 3 não cobre: o valor ainda NÃO existe
 *     no servidor — ele está sendo digitado. Sem o eco, `123456` vira R$ 123.456,00 na cabeça
 *     de quem digitou e R$ 1.234,56 no banco, e a diferença só aparece num lucro por lead
 *     100x errado três telas adiante, sem nenhum erro no meio do caminho. O que a prévia
 *     NUNCA faz é classificar a faixa: ela mostra o VALOR que vai sair, e quem diz em que
 *     faixa ele caiu continua sendo o servidor, depois de gravar.
 */
import { ChevronsUp, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { formatCurrency } from '@mgdlead/ui';
import type {
  EarningsBand,
  EarningsMarketplaceName,
  EarningsMarketplaceSlice,
  EarningsMembersStatus,
} from '../../lib/api';

/* ========================================================================== */
/*  Os marketplaces                                                            */
/* ========================================================================== */

/**
 * Como cada marketplace se chama na tela.
 *
 * Mesmo padrão das faixas: o servidor manda o CÓDIGO, a tela escolhe a palavra. O que a tela
 * **não** faz é inventar a lista — ela percorre `dia.marketplaces`, que vem com os três
 * sempre, na ordem do produto. Uma lista própria aqui ficaria para trás no dia em que
 * entrasse um quarto marketplace, e o dinheiro dele apareceria no total sem aparecer em
 * lugar nenhum.
 */
export const MARKETPLACE: Record<EarningsMarketplaceName, string> = {
  SHOPEE: 'Shopee',
  AMAZON: 'Amazon',
  MERCADO_LIVRE: 'Mercado Livre',
};

/**
 * O rótulo do campo de lançamento, com o artigo certo: *"Comissão **da** Shopee"*, *"Comissão
 * **do** Mercado Livre"*. Duas palavras de português que, erradas, fazem a tela parecer
 * traduzida por máquina — e esta é a tela que o dono abre todo fim de expediente.
 */
export const ROTULO_DO_CAMPO: Record<EarningsMarketplaceName, string> = {
  SHOPEE: 'Comissão da Shopee',
  AMAZON: 'Comissão da Amazon',
  MERCADO_LIVRE: 'Comissão do Mercado Livre',
};

/**
 * Por que NÃO existe faixa (semáforo) por marketplace.
 *
 * A régua do produto — R$0,03 normal, R$0,05 a R$0,07 ideal — é do mercado de afiliado da
 * **Shopee**, dita pelo dono sobre a Shopee. Amazon e Mercado Livre têm comissão e ticket
 * diferentes: pintar a fatia da Amazon de vermelho por ela render menos por lead diria que
 * algo está errado quando talvez esteja ótimo, e semáforo que erra ensina a ignorar
 * semáforo. O geral continua com faixa, porque é ele que paga o tráfego; por marketplace a
 * tela mostra **o valor e a participação**, e não julga.
 *
 * Inventar faixa por marketplace exige dado que hoje não existe. Quando existir, ela nasce em
 * `workspace_settings`, como a atual — nunca cravada na tela.
 */
export const POR_QUE_SEM_FAIXA_POR_MARKETPLACE =
  'A faixa (baixo, normal, ideal, muito bom) é calibrada no mercado de afiliado da Shopee, então ela classifica só o número geral — o que paga o tráfego. Por marketplace a tela mostra quanto cada um rendeu por lead e quanto pesou no dia, sem julgar.';

/**
 * A participação em pontos-base virando texto. `2500` -> `"25%"`.
 *
 * `null` continua `null`: sem total para comparar não existe participação, e `0%` afirmaria
 * uma proporção que ninguém mediu. Não há conta de métrica aqui — é troca de unidade de um
 * inteiro que já veio pronto do servidor.
 */
export function participacaoEmTexto(basisPoints: number | null): string | null {
  if (basisPoints === null) return null;
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(basisPoints / 100)}%`;
}

/**
 * A frase de um marketplace que **não foi lançado** — e ela existe para não virar R$ 0,00.
 *
 * "A Amazon não vendeu nada hoje" e "ninguém lançou a Amazon hoje" mandam fazer coisas
 * diferentes: a primeira é um fato encerrado, a segunda é uma tarefa. Um painel que mostrasse
 * zero nas duas transformaria o esquecimento em fato consumado (invariante 8).
 */
export function naoLancadoEm(marketplace: EarningsMarketplaceName): string {
  return `${MARKETPLACE[marketplace]} ainda não lançada neste dia — o que é diferente de ter vendido R$ 0,00.`;
}

/** Só os marketplaces que têm lançamento. Vazio é "o dia inteiro não foi lançado". */
export function lancados(
  marketplaces: readonly EarningsMarketplaceSlice[],
): EarningsMarketplaceSlice[] {
  return marketplaces.filter((m) => m.amountCents !== null);
}

/* ========================================================================== */
/*  As faixas                                                                  */
/* ========================================================================== */

export interface BandaSpec {
  /** Como a faixa se chama na tela. É o segundo canal, além da cor. */
  label: string;
  /** O terceiro canal: FORMA. A tela é olhada às 23h, no celular, e por daltônico. */
  icon: LucideIcon;
  tone: 'warning' | 'neutral' | 'success';
  /**
   * O que a faixa quer dizer para o negócio — **sem número**. A régua é do servidor; repetir
   * "R$ 0,03" aqui seria cravar na tela um limite que a tela não conhece.
   */
  significado: string;
}

/**
 * `BAIXO` é `warning`, não `danger`: lead barato demais é um sinal de negócio, não uma
 * falha do sistema. Pintar de vermelho o que não quebrou ensina a ignorar o vermelho.
 *
 * `IDEAL` e `MUITO_BOM` dividem o mesmo tom (os dois são boa notícia) e se distinguem por
 * ÍCONE e por TEXTO — que é justamente a regra de não deixar a cor ser o único canal.
 */
export const BANDA: Record<EarningsBand, BandaSpec> = {
  BAIXO: {
    label: 'Baixo',
    icon: TrendingDown,
    tone: 'warning',
    significado:
      'Abaixo do que este mercado considera normal: o lead está entrando, mas devolvendo pouco.',
  },
  NORMAL: {
    label: 'Normal',
    icon: Minus,
    tone: 'neutral',
    significado: 'Dentro do normal do mercado.',
  },
  IDEAL: {
    label: 'Ideal',
    icon: TrendingUp,
    tone: 'success',
    significado: 'Na faixa que uma conta rodando em escala persegue.',
  },
  MUITO_BOM: {
    label: 'Muito bom',
    icon: ChevronsUp,
    tone: 'success',
    significado: 'Acima da faixa ideal — é lead que entra muito bem.',
  },
};

/* ========================================================================== */
/*  Por que não há denominador                                                 */
/* ========================================================================== */

/**
 * Sem gente nos grupos não existe lucro por lead — e os DOIS motivos de não haver gente
 * mandam fazer coisas diferentes, então eles não podem virar a mesma frase.
 *
 * `SEM_GRUPOS_ATIVOS` é "não havia operação nesse dia": não há nada a consertar.
 * `COBERTURA_INCOMPLETA` é "faltou medir": o número existiria, e dividir pelo denominador
 * pela metade daria um lucro por lead MAIOR que o real — notícia boa inventada, que é o pior
 * jeito de errar aqui.
 *
 * `null` no caso medido: quando o denominador vale, não há o que explicar.
 */
export function motivoDoDenominador(status: EarningsMembersStatus): string | null {
  if (status === 'SEM_GRUPOS_ATIVOS') {
    return 'Nenhum grupo estava ativo neste dia, então não há gente para dividir — e isso é diferente de ter dado zero.';
  }
  if (status === 'COBERTURA_INCOMPLETA') {
    return 'Pelo menos um grupo ativo ficou sem contagem de participantes neste dia, então o denominador não fecha. Dividir pelo que sobrou daria um lucro por lead maior que o real.';
  }
  return null;
}

/** A frase que explica de onde a classificação vem. Vai no rodapé do painel. */
export const FONTE_DA_FAIXA =
  'A faixa (baixo, normal, ideal, muito bom) é classificada pelo servidor a partir do lucro por lead do dia. A tela não refaz essa conta nem guarda os limites dela.';

/* ========================================================================== */
/*  Dinheiro — o eco da entrada                                                */
/* ========================================================================== */

/** Teto de dígitos do campo: R$ 999.999.999,99. Acima disso é dedo escorregando no teclado. */
export const MAX_DIGITOS_VALOR = 11;

/**
 * Só os dígitos, sem zero à esquerda e com teto.
 *
 * É o coração do campo de dinheiro: **todo dígito é centavo**. `123456` é R$ 1.234,56;
 * `1.234,56` também, porque os separadores são jogados fora. As duas formas de digitar o
 * mesmo valor chegam no mesmo número — que é o contrário de um campo que aceita "1234,56" e
 * "123456" como dois valores 100x diferentes sem avisar ninguém.
 */
export function apenasDigitos(entrada: string): string {
  return entrada
    .replace(/\D/g, '')
    .replace(/^0+(?=\d)/, '')
    .slice(0, MAX_DIGITOS_VALOR);
}

/** Os dígitos como número de centavos. String vazia é vazio, e vazio **não é zero**. */
export function centavosDeDigitos(digitos: string): number | null {
  const limpos = apenasDigitos(digitos);
  if (limpos.length === 0) return null;
  return Number(limpos);
}

/** `123456` -> `"1.234,56"` — o que o campo mostra enquanto a pessoa digita. */
export function mascaraDeCentavos(digitos: string): string {
  const centavos = centavosDeDigitos(digitos);
  if (centavos === null) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(centavos / 100);
}

/** `123456` -> `"R$ 1.234,56"` — o eco, com a moeda escrita, para não restar dúvida. */
export function reaisDeCentavos(centavos: number): string {
  return formatCurrency(centavos / 100);
}

/* ========================================================================== */
/*  Lucro por lead                                                             */
/* ========================================================================== */

/**
 * Milésimos de centavo viram reais na tela. `3000` -> `"R$ 0,0300"`.
 *
 * Quatro casas porque é onde a decisão mora: a diferença entre um lead normal e um lead ideal
 * está na SEGUNDA casa do centavo, e arredondar para dois centavos transformaria todas as
 * quatro faixas em "R$ 0,03" ou "R$ 0,05". Aqui não há conta de métrica: é troca de unidade
 * de um número que já veio pronto.
 */
export function lucroPorLeadEmReais(millicents: number): number {
  return millicents / 100_000;
}

export function formatarLucroPorLead(millicents: number): string {
  return formatCurrency(lucroPorLeadEmReais(millicents), 'BRL', 4);
}

/**
 * A PRÉVIA do lucro por lead do valor que está sendo digitado — em milésimos de centavo.
 *
 * `null` quando não existe denominador: sem contagem de participantes do dia, não há lucro
 * por lead nenhum, e mostrar zero inventaria uma medição. É a mesma regra que vale para o
 * número já gravado; a prévia não tem licença para ser mais otimista que o painel.
 *
 * Não arredonda de propósito: o valor exibido sai da divisão crua, e o número inteiro que
 * vale é o que o servidor devolver depois de gravar.
 */
export function previaLucroPorLead(centavos: number, participantes: number | null): number | null {
  if (participantes === null || participantes <= 0) return null;
  return (centavos * 1000) / participantes;
}

/* ========================================================================== */
/*  Dias do negócio                                                            */
/* ========================================================================== */

/**
 * Aritmética de dia do negócio em `YYYY-MM-DD`, **em UTC de propósito** — a mesma decisão
 * (e o mesmo motivo) do `addDays` do servidor: o dia do negócio já é um rótulo, não um
 * instante, então voltar um dia é voltar uma casa no calendário, não 24 horas de relógio.
 *
 * O ponto de partida **nunca** é `new Date()`: é sempre o "hoje" que o servidor resolveu no
 * fuso do workspace. Um relógio de navegador em Lisboa faria o painel pedir o dia errado às
 * 22h de São Paulo, e o dono só descobriria comparando com o número de outra pessoa.
 */
export function diasAntes(diaDoNegocio: string, dias: number): string {
  const d = new Date(`${diaDoNegocio}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - dias);
  return d.toISOString().slice(0, 10);
}

/** `2026-09-17` -> `17/09/2026`. */
export function diaLongo(diaDoNegocio: string): string {
  const [ano = '', mes = '', dia = ''] = diaDoNegocio.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** `2026-09-17` -> `17/09`. */
export function diaCurto(diaDoNegocio: string): string {
  const [, mes = '', dia = ''] = diaDoNegocio.split('-');
  return `${dia}/${mes}`;
}

/** "hoje" só é dito quando o servidor confirmou que é hoje. */
export function rotuloDoDia(diaDoNegocio: string, hoje: string | null): string {
  return diaDoNegocio === hoje ? `hoje, ${diaLongo(diaDoNegocio)}` : diaLongo(diaDoNegocio);
}
